/* eslint-disable @atlaskit/design-system/consistent-css-prop-usage */

import React, { useState } from 'react';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { cssMap } from '@atlaskit/css';
import { Box, Stack, Inline } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { SelectCard } from './card';
import { GroupHeading } from './group-heading';
import { type SelectListProps } from './types';

const styles = cssMap({
	groupContainer: {
		// Reset default fieldset browser styles
		border: 'none',
		padding: '0',
		margin: '0',
		minWidth: '0',
	},
	itemsMargin: {
		marginTop: token('space.150'),
	},
	rowContainer: {
		flexWrap: 'wrap',
		'@media (min-width: 48rem)': {
			flexWrap: 'nowrap',
		},
	},
	gridContainer: {
		gap: token('space.150'),
		display: 'grid',
	},
});

const SelectListItemsContainer = ({
	direction,
	children,
	gridColumns,
}: {
	children: React.ReactNode;
	direction: 'column' | 'row' | 'grid';
	gridColumns?: number;
}) => {
	switch (direction) {
		case 'row':
			return (
				<Inline space="space.150" alignBlock="stretch" xcss={styles.rowContainer}>
					{children}
				</Inline>
			);
		case 'column':
			return <Stack space="space.150">{children}</Stack>;
		case 'grid':
			// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- dynamic grid columns require inline style
			return (
				<Box
					xcss={styles.gridContainer}
					style={{
						gridTemplateColumns: gridColumns
							? `repeat(${gridColumns}, 1fr)`
							: 'repeat(auto-fill, minmax(200px, 1fr))',
					}}
				>
					{children}
				</Box>
			);
	}
};

const SelectList = (props: SelectListProps): React.JSX.Element => {
	const { groups, testId = 'select-list', direction = 'column' } = props;

	// Single-select state
	const defaultSelected = !props.isMulti ? props.selectedValue || props.defaultValue : undefined;
	const [selected, setSelected] = useState<string | undefined>(defaultSelected);

	// Multi-select state
	const defaultSelectedMulti = props.isMulti ? props.defaultValues || [] : [];
	const [selectedMulti, setSelectedMulti] = useState<string[]>(defaultSelectedMulti);

	const handleChange = (
		event: React.MouseEvent<HTMLElement, MouseEvent> | React.ChangeEvent<HTMLInputElement>,
		newSelection: string,
	) => {
		if (props.isMulti) {
			// props is narrowed to SelectListMultiProps here — no cast needed
			const currentlySelected = selectedMulti.includes(newSelection)
				? selectedMulti.filter((v) => v !== newSelection)
				: [...selectedMulti, newSelection];
			setSelectedMulti(currentlySelected);
			props.onChange(event, currentlySelected);
		} else {
			// props is narrowed to SelectListSingleProps here — no cast needed
			setSelected(newSelection);
			props.onChange(event, newSelection);
		}
	};

	return (
		<Stack space="space.200" testId={testId}>
			{groups.map(
				({
					items,
					groupId,
					groupName,
					groupDescriptionIcon,
					groupDescription,
					groupDescriptionInfoLink,
					groupDescriptionInfoLinkMessage,
					groupAriaLabel,
				}) => {
					return (
						<Box
							as="fieldset"
							aria-label={groupAriaLabel}
							xcss={styles.groupContainer}
							key={groupId}
						>
							{/* GroupHeading renders as <legend> — must be direct first child of fieldset */}
							<GroupHeading
								testId={testId}
								groupId={groupId}
								groupName={groupName}
								groupDescriptionIcon={groupDescriptionIcon}
								groupDescription={groupDescription}
								groupDescriptionInfoLink={groupDescriptionInfoLink}
								groupDescriptionInfoLinkMessage={groupDescriptionInfoLinkMessage}
							/>
							<Box xcss={styles.itemsMargin}>
								<SelectListItemsContainer direction={direction} gridColumns={props.columns}>
									{items.map((itemProps, index) => {
										return (
											<SelectCard
												testId={`${testId}-card-${index}`}
												key={itemProps.id}
												handleChange={handleChange}
												selected={selected}
												selectedMulti={props.isMulti ? selectedMulti : undefined}
												isMulti={props.isMulti}
												direction={direction}
												radioGroupName={!props.isMulti ? props.radioGroupName : undefined}
												{...itemProps}
											/>
										);
									})}
								</SelectListItemsContainer>
							</Box>
						</Box>
					);
				},
			)}
		</Stack>
	);
};

export default SelectList;
